import { useCallback, useEffect, useRef, useState } from 'react';

export interface UsePaginatedDexieDataOptions<T, C> {
  /** Fetches the next page, given the cursor of the last item already loaded (`undefined` for the first page). */
  fetchPage: (cursor: C | undefined) => Promise<T[]>;
  /** Derives the cursor for a given item, used to fetch the page that follows it. */
  getCursor: (lastItem: T) => C;
  /** Must match the page size `fetchPage` itself applies (e.g. its own `.limit()`) - used to detect the last page. */
  pageSize: number;
  /** When any of these change, the list resets and refetches from the start. Defaults to `[]` (fetch once, on mount). */
  deps?: unknown[];
}

export interface UsePaginatedDexieDataReturn<T> {
  itemList: T[];
  fetchMore(): Promise<void>;
  addItem(item: T): void;
  removeItem(id: string): void;
  updateItem(item: T): void;
  resetList(): void;
  hasMoreData: boolean;
}

interface InternalState<T, C> {
  items: T[];
  hasMoreData: boolean;
  cursor: C | undefined;
}

const freshState = <T, C>(): InternalState<T, C> => ({ items: [], hasMoreData: true, cursor: undefined });

export const usePaginatedDexieData = <T extends { id: string }, C = unknown>({
  fetchPage,
  getCursor,
  pageSize,
  deps = [],
}: UsePaginatedDexieDataOptions<T, C>): UsePaginatedDexieDataReturn<T> => {
  const [state, setState] = useState<InternalState<T, C>>(freshState<T, C>);
  const stateRef = useRef(state);

  const epochRef = useRef(0);
  const fetchingRef = useRef(false);

  const fetchPageRef = useRef(fetchPage);
  const getCursorRef = useRef(getCursor);
  useEffect(() => {
    fetchPageRef.current = fetchPage;
    getCursorRef.current = getCursor;
  });

  const fetchMore = useCallback(async () => {
    if (fetchingRef.current || !stateRef.current.hasMoreData) return;

    fetchingRef.current = true;
    const requestEpoch = epochRef.current;

    // React Compiler can't lower try/finally, so cleanup is duplicated below instead of using `finally`.
    try {
      const newItems = await fetchPageRef.current(stateRef.current.cursor);
      if (requestEpoch !== epochRef.current) return;

      const lastItem = newItems.at(-1);
      let cursor = stateRef.current.cursor;
      if (lastItem !== undefined) {
        cursor = getCursorRef.current(lastItem);
      }

      const next: InternalState<T, C> = {
        items: stateRef.current.items.concat(newItems),
        hasMoreData: newItems.length === pageSize,
        cursor,
      };
      stateRef.current = next;
      setState(next);
    } catch (error) {
      if (requestEpoch === epochRef.current) {
        fetchingRef.current = false;
      }
      throw error;
    }

    if (requestEpoch === epochRef.current) {
      fetchingRef.current = false;
    }
  }, [pageSize]);

  const fetchMoreRef = useRef(fetchMore);
  useEffect(() => {
    fetchMoreRef.current = fetchMore;
  }, [fetchMore]);

  const resetList = useCallback(() => {
    epochRef.current += 1;
    fetchingRef.current = false;
    const fresh = freshState<T, C>();
    stateRef.current = fresh;
    setState(fresh);
    fetchMoreRef.current();
  }, []);

  // `deps` is caller-supplied and intentionally drives this effect directly
  useEffect(() => {
    resetList();
  }, deps);

  const addItem = useCallback((item: T) => {
    const next: InternalState<T, C> = { ...stateRef.current, items: [item].concat(stateRef.current.items) };
    stateRef.current = next;
    setState(next);
  }, []);

  const removeItem = useCallback((id: string) => {
    const next: InternalState<T, C> = {
      ...stateRef.current,
      items: stateRef.current.items.filter((existing) => existing.id !== id),
    };
    stateRef.current = next;
    setState(next);
  }, []);

  const updateItem = useCallback((item: T) => {
    const index = stateRef.current.items.findIndex((existing) => existing.id === item.id);
    if (index < 0) return;

    const items = stateRef.current.items.slice();
    items[index] = item;
    const next: InternalState<T, C> = { ...stateRef.current, items };
    stateRef.current = next;
    setState(next);
  }, []);

  return {
    itemList: state.items,
    fetchMore,
    addItem,
    removeItem,
    updateItem,
    resetList,
    hasMoreData: state.hasMoreData,
  };
};
